/**
 * Gold Diggers Club — date-window leaderboard + metrics
 *
 * Phase 9 Diggers ended 5 Aug 23:59 SAST.
 * Active Diggers = Birthday Extension / Phase 10 (6 Aug – 13 Sep), scoring
 * investment phase ids 10 + 11 in that window only.
 * Phase 9+ windows use competition_submitted_at (payment submit), not approval created_at.
 */

import { fetchAllRows } from './fetchAllRows.js';

export const COMPETITION_EXCLUDED_METHODS = [
  'Consolidated',
  'Consolidated Remainder',
  'Commission Shares',
  'Consolidated Bonus',
  'Legacy Bonus',
  'Received Transfers',
  'Glitch Correction',
  'USDT_COMMISSION_CONVERSION',
  'Performance Pool',
  'Gold Diggers Pool Claim',
];

/** Synthetic commission-share rows. Same families the daily pool already ignores. */
export const COMPETITION_EXCLUDED_PREFIXES = [
  'COMMISSION_LEVEL_',
  'COMMISSION_FIX_',
  'COMMISSION_CORRECTION_',
];

/**
 * Club sales are outside money paid for shares.
 * COMMISSION_LEVEL_1 is a commission fraction minted as shares (Leticia's $5 / 0.05 share),
 * not a new referral purchase. Counting it scores the original sale twice.
 */
export function isClubSaleMethod(method) {
  const m = String(method || '').trim();
  if (!m) return false;
  if (COMPETITION_EXCLUDED_METHODS.includes(m)) return false;
  const upper = m.toUpperCase();
  return !COMPETITION_EXCLUDED_PREFIXES.some((prefix) => upper.startsWith(prefix));
}

/** Display class for a referral row. Does not expose the raw payment method. */
export function clubPurchaseRowKind(method) {
  const m = String(method || '').trim();
  const upper = m.toUpperCase();
  if (
    m === 'Commission Shares' ||
    upper === 'USDT_COMMISSION_CONVERSION' ||
    COMPETITION_EXCLUDED_PREFIXES.some((prefix) => upper.startsWith(prefix))
  ) return 'commission';
  if (upper.includes('GIFT')) return 'gift';
  if (!isClubSaleMethod(m)) return 'excluded';
  return 'sale';
}

const CLUB_AMOUNT_TOL = 0.009;

/**
 * Club dollars cannot exceed shares × the phase price.
 * Cash above that (a wallet refund, a chain overpay stored on the purchase)
 * is not a share payment and must not raise the prize pool.
 * A total below the cap is left as recorded.
 */
export function clubCountedSaleAmount(row, phasePrice) {
  const total = parseFloat(row?.total_amount) || 0;
  const shares = parseFloat(row?.shares_purchased) || 0;
  const fromPhase = parseFloat(phasePrice);
  const fromRow = parseFloat(row?.price_per_share) || 0;
  const price = fromPhase > 0 ? fromPhase : fromRow;
  if (!(shares > 0) || !(price > 0) || !(total > 0)) return total;
  const cap = Math.round(shares * price * 100) / 100;
  if (total > cap + CLUB_AMOUNT_TOL) return cap;
  return total;
}

export async function applyClubSaleAmounts(supabase, purchases) {
  const rows = purchases || [];
  if (!rows.length) return rows;
  const phaseIds = [...new Set(rows.map((p) => Number(p.phase_id)).filter((id) => id > 0))];
  const priceByPhase = new Map();
  if (phaseIds.length) {
    const { data } = await supabase
      .from('investment_phases')
      .select('id, price_per_share')
      .in('id', phaseIds);
    for (const phase of data || []) {
      priceByPhase.set(Number(phase.id), parseFloat(phase.price_per_share) || 0);
    }
  }
  for (const p of rows) {
    p.total_amount = clubCountedSaleAmount(p, priceByPhase.get(Number(p.phase_id)));
  }
  return rows;
}

const EXCLUDED_IN_FILTER = `(${COMPETITION_EXCLUDED_METHODS.map((m) => `"${m}"`).join(',')})`;
const IN_CHUNK = 200;

/** Phase 9 investment id + Phase 10 investment id — Birthday Extension bridge window */
export const BIRTHDAY_EXTENSION_PHASE_ID = 10; // phase_number 9
export const PHASE10_PHASE_ID = 11; // phase_number 10
export const COMBINED_DIGGERS_PHASE_IDS = [BIRTHDAY_EXTENSION_PHASE_ID, PHASE10_PHASE_ID];

export const PHASE9_DIGGERS_END_SAST = '2026-08-05T23:59:59+02:00';
export const AUG_DIGGERS_START_SAST = '2026-08-06T00:00:00+02:00';
export const AUG_DIGGERS_END_SAST = '2026-09-13T23:59:59+02:00';

/**
 * Weekly Club scoring starts at Mine Production (id 82).
 * Live phase_number is 10.1, not 11. A `>= 11` check left that phase on the
 * full Sep 13–Oct 14 window (monthly Top 50) while the Club is weekly.
 * Phase 10 (phase_number 10.0, id 11) and earlier stay monthly.
 */
export const MINE_PRODUCTION_PHASE_ID = 82;
export const MINE_PRODUCTION_PHASE_NUMBER = 10.1;

export function usesWeeklyDiggers(phase) {
  if (!phase) return false;
  if (Number(phase.id) === MINE_PRODUCTION_PHASE_ID) return true;
  if (phase.phase_number != null && !Number.isNaN(Number(phase.phase_number))) {
    return Number(phase.phase_number) > 10;
  }
  return false;
}

/** Monthly Club (Phase 10 and earlier): 15% of window revenue, Top 50. */
export const MONTHLY_DIGGERS_POOL_CONFIG = {
  prize_pool_percent: 15,
  payout_slots: 50,
  payout_percentages: {
    1: 15, 2: 12, 3: 10,
    '4-10': 3, '11-20': 2, '21-30': 1, '31-50': 0.6,
  },
};

/** Weekly Club (Mine Production Phase onward): 10% of week revenue, Top 100. */
export const WEEKLY_DIGGERS_POOL_CONFIG = {
  prize_pool_percent: 10,
  payout_slots: 100,
  payout_percentages: {
    1: 8, 2: 6, 3: 4,
    '4-10': 3, '11-20': 2, '21-40': 1, '41-60': 0.5, '61-100': 0.275,
  },
};

/** Club UI only (Mine Production+). Does not change investment_phases.price_per_share or commission payouts. */
export const WEEKLY_DIGGERS_DISPLAY_SHARE_PRICE = 100;
export const WEEKLY_DIGGERS_DISPLAY_COMMISSION_USDT = 10;

export function getClubPoolConfig({ weeklyEnabled, dbConfig } = {}) {
  if (weeklyEnabled) {
    return { ...WEEKLY_DIGGERS_POOL_CONFIG };
  }
  if (!dbConfig) return { ...MONTHLY_DIGGERS_POOL_CONFIG };
  return {
    prize_pool_percent: dbConfig.prize_pool_percent ?? MONTHLY_DIGGERS_POOL_CONFIG.prize_pool_percent,
    payout_slots: MONTHLY_DIGGERS_POOL_CONFIG.payout_slots,
    payout_percentages: dbConfig.payout_percentages ?? MONTHLY_DIGGERS_POOL_CONFIG.payout_percentages,
  };
}

export function getClubPrizePercent(rank, payoutPcts) {
  const pcts = payoutPcts || MONTHLY_DIGGERS_POOL_CONFIG.payout_percentages;
  if (pcts[rank] !== undefined) return Number(pcts[rank]);
  for (const key of Object.keys(pcts)) {
    if (!String(key).includes('-')) continue;
    const [lo, hi] = String(key).split('-').map(Number);
    if (rank >= lo && rank <= hi) return Number(pcts[key]);
  }
  return 0;
}

/**
 * Whole-dollar prizes for ranks 1..slots. Percentages stay as published.
 * Independent rounding of each rank overshoots the pool (week-3 $2,480 was paying $2,484).
 * Largest remainder keeps the sum equal to the rounded pool. Tied remainders go to the better rank.
 *
 * qualifiedCount: people who actually qualified. Omit it, or pass a full field, and each
 * rank keeps its published share. When the field is short, empty-rank dollars are split
 * equally across the qualified list and added on top of each rank prize. Extra dollars
 * go to the better ranks, matching the performance pool remainder walk from rank 1.
 * The payable amounts still sum to the pool. Empty ranks pay $0.
 */
export function allocateClubPrizeAmounts(poolDollars, payoutPcts, slots, qualifiedCount = null) {
  const pool = Math.max(0, Math.round(Number(poolDollars) || 0));
  const max = Math.max(0, Math.round(Number(slots) || 0));
  const rows = [];
  for (let rank = 1; rank <= max; rank++) {
    const pct = getClubPrizePercent(rank, payoutPcts);
    if (!(pct > 0) || pool === 0) {
      rows.push({ rank, pct, amount: 0, remainder: 0 });
      continue;
    }
    const exact = (pool * Math.round(pct * 1000)) / 100000;
    const amount = Math.floor(exact + 1e-9);
    rows.push({ rank, pct, amount, remainder: exact - amount });
  }
  let leftover = pool - rows.reduce((sum, row) => sum + row.amount, 0);
  if (leftover > 0) {
    const order = rows
      .filter((row) => row.pct > 0)
      .sort((a, b) => (b.remainder - a.remainder) || (a.rank - b.rank));
    for (let i = 0; i < order.length && leftover > 0; i++) {
      order[i].amount += 1;
      leftover -= 1;
    }
  }
  const scheduled = rows.map(({ rank, pct, amount }) => ({ rank, pct, amount }));
  const qualified = qualifiedCount == null
    ? max
    : Math.max(0, Math.min(max, Math.round(Number(qualifiedCount) || 0)));
  if (qualified === 0) {
    return scheduled.map((row) => ({ ...row, rankPrize: row.amount, bonus: 0, amount: 0 }));
  }
  if (qualified >= max) {
    return scheduled.map((row) => ({ ...row, rankPrize: row.amount, bonus: 0 }));
  }
  const unfilledDollars = scheduled.slice(qualified).reduce((sum, row) => sum + row.amount, 0);
  const baseShare = Math.floor(unfilledDollars / qualified);
  let extra = unfilledDollars - baseShare * qualified;
  return scheduled.map((row, index) => {
    if (row.rank > qualified) {
      return { ...row, rankPrize: row.amount, bonus: 0, amount: 0 };
    }
    const bonus = baseShare + (index < extra ? 1 : 0);
    return { ...row, rankPrize: row.amount, bonus, amount: row.amount + bonus };
  });
}

export function sumClubPayoutPercent(payoutPcts) {
  let sum = 0;
  for (const [key, value] of Object.entries(payoutPcts || {})) {
    const n = Number(value) || 0;
    if (String(key).includes('-')) {
      const [lo, hi] = String(key).split('-').map(Number);
      sum += n * (hi - lo + 1);
    } else {
      sum += n;
    }
  }
  return sum;
}

/** Phase 9 (investment id 10) onward: Club window uses payment submit time. */
export function usesSubmitTimeScoring(phaseIds) {
  return (phaseIds || []).some((id) => Number(id) >= BIRTHDAY_EXTENSION_PHASE_ID);
}

/** Score clock: competition_submitted_at (CPT submit), else created_at. */
export function competitionScoreTime(row) {
  return row?.competition_submitted_at || row?.created_at || null;
}

export function purchaseInCompetitionWindow(row, start, end) {
  const raw = competitionScoreTime(row);
  if (!raw) return false;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return false;
  if (start && t < new Date(start).getTime()) return false;
  if (end && t > new Date(end).getTime()) return false;
  return true;
}

function quoteTs(iso) {
  return `"${String(iso).replace(/"/g, '')}"`;
}

/** PostgREST window: submit in range, or null submit + created_at in range. */
export function applyCompetitionTimeWindow(query, { start, end, phaseIds } = {}) {
  if (!start && !end) return query;
  if (!usesSubmitTimeScoring(phaseIds)) {
    if (start) query = query.gte('created_at', start);
    if (end) query = query.lte('created_at', end);
    return query;
  }
  const submitParts = [];
  const createdParts = [];
  if (start) {
    submitParts.push(`competition_submitted_at.gte.${quoteTs(start)}`);
    createdParts.push(`created_at.gte.${quoteTs(start)}`);
  }
  if (end) {
    submitParts.push(`competition_submitted_at.lte.${quoteTs(end)}`);
    createdParts.push(`created_at.lte.${quoteTs(end)}`);
  }
  const submitExpr = submitParts.length === 1 ? submitParts[0] : `and(${submitParts.join(',')})`;
  const createdExpr = createdParts.length === 1 ? createdParts[0] : `and(${createdParts.join(',')})`;
  return query.or(`${submitExpr},and(competition_submitted_at.is.null,${createdExpr})`);
}

async function fetchInChunks(supabase, table, select, column, ids, extra = (q) => q) {
  if (!ids.length) return [];
  const out = [];
  for (let i = 0; i < ids.length; i += IN_CHUNK) {
    const chunk = ids.slice(i, i + IN_CHUNK);
    let q = supabase.from(table).select(select).in(column, chunk);
    q = extra(q);
    // v4.8.251: same unordered-pagination hazard as the purchase reads. A chunk of
    // IN_CHUNK ids normally fits in one page, but that is incidental — order on the
    // filtered column so the result stays stable if a chunk ever pages.
    q = q.order(column, { ascending: true });
    const rows = await fetchAllRows(q);
    out.push(...rows);
  }
  return out;
}

function normalizePhaseIds(opts) {
  if (Array.isArray(opts.phaseIds) && opts.phaseIds.length) {
    return [...new Set(opts.phaseIds.map(Number).filter((n) => !Number.isNaN(n)))];
  }
  if (opts.phaseId != null) return [Number(opts.phaseId)];
  return [];
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {{
 *   phaseId?: number,
 *   phaseIds?: number[],
 *   start?: string | null,
 *   end?: string | null,
 *   excludedUserIds?: number[],
 * }} opts
 */
export async function computeCompetitionLeaderboard(supabase, opts) {
  const {
    start = null,
    end = null,
    excludedUserIds = [],
  } = opts;

  const phaseIds = normalizePhaseIds(opts);
  if (!phaseIds.length) throw new Error('phaseId or phaseIds required');

  const excluded = new Set((excludedUserIds || []).map(Number));
  const singlePhase = phaseIds.length === 1 ? phaseIds[0] : null;

  // Fast path: single phase, no date window → existing RPC
  if (singlePhase != null && !start && !end) {
    const { data, error } = await supabase.rpc('get_leaderboard', {
      p_phase_id: singlePhase,
      p_excluded_ids: [...excluded],
    });
    if (error) {
      const fallback = await supabase.rpc('get_leaderboard', { p_phase_id: singlePhase });
      if (fallback.error) throw fallback.error;
      const leaderboard = (fallback.data || [])
        .filter((e) => !excluded.has(Number(e.referrer_id)))
        .map((e) => ({
          referrer_id: e.referrer_id,
          username: e.username,
          total_volume: parseFloat(e.total_volume) || 0,
          unique_referrals: parseInt(e.unique_referrals, 10) || 0,
        }))
        .sort((a, b) => b.total_volume - a.total_volume);
      const metrics = await computeCompetitionMetrics(supabase, { phaseIds, start, end });
      return { leaderboard, metrics, source: 'rpc', phaseIds };
    }
    const leaderboard = (data || []).map((e) => ({
      referrer_id: e.referrer_id,
      username: e.username,
      total_volume: parseFloat(e.total_volume) || 0,
      unique_referrals: parseInt(e.unique_referrals, 10) || 0,
    }));
    const metrics = await computeCompetitionMetrics(supabase, { phaseIds, start, end });
    return { leaderboard, metrics, source: 'rpc', phaseIds };
  }

  // Date-window and/or multi-phase path
  let purchaseQuery = supabase
    .from('aureus_share_purchases')
    .select('id, user_id, total_amount, shares_purchased, price_per_share, phase_id, payment_method, created_at, competition_submitted_at')
    .in('phase_id', phaseIds)
    .in('status', ['active', 'consolidated'])
    .not('payment_method', 'in', EXCLUDED_IN_FILTER)
    // v4.8.251 CORRECTNESS: fetchAllRows() pages with .range(). PostgreSQL gives NO
    // ordering guarantee between pages unless the query is explicitly ordered, so an
    // unordered paged read silently returns some rows twice and skips others. Measured on
    // prod for this exact query: 11,185 rows fetched but only 10,671 unique — 514 distinct
    // purchases were dropped from every leaderboard computation, which is what removed
    // referrer 8388's buyers 14603 ($500) and 14638 ($400) and cost her 6 places.
    // `id` is the primary key, so it is a stable, unique, total ordering.
    .order('id', { ascending: true });
  purchaseQuery = applyCompetitionTimeWindow(purchaseQuery, { start, end, phaseIds });

  let purchases = await fetchAllRows(purchaseQuery);
  purchases = purchases.filter((p) => isClubSaleMethod(p.payment_method));
  if (start || end) {
    purchases = purchases.filter((p) => purchaseInCompetitionWindow(p, start, end));
  }
  await applyClubSaleAmounts(supabase, purchases);
  if (!purchases.length) {
    return { leaderboard: [], metrics: emptyMetrics(), source: 'window', phaseIds };
  }

  const volumeByBuyer = new Map();
  for (const p of purchases) {
    volumeByBuyer.set(p.user_id, (volumeByBuyer.get(p.user_id) || 0) + (parseFloat(p.total_amount) || 0));
  }
  const buyerIds = [...volumeByBuyer.keys()];

  const referralRows = await fetchInChunks(
    supabase,
    'referrals',
    'referrer_id, referred_id',
    'referred_id',
    buyerIds,
    (q) => q.eq('status', 'active')
  );

  const referrerVolume = new Map();
  for (const ref of referralRows) {
    const buyerVol = volumeByBuyer.get(ref.referred_id) || 0;
    if (buyerVol <= 0) continue;
    if (excluded.has(Number(ref.referrer_id))) continue;
    let entry = referrerVolume.get(ref.referrer_id);
    if (!entry) {
      entry = { volume: 0, buyers: new Set() };
      referrerVolume.set(ref.referrer_id, entry);
    }
    entry.volume += buyerVol;
    entry.buyers.add(ref.referred_id);
  }

  const referrerIds = [...referrerVolume.keys()];
  const users = await fetchInChunks(supabase, 'users', 'id, username', 'id', referrerIds);
  const usernameById = new Map(users.map((u) => [u.id, u.username]));

  const leaderboard = [...referrerVolume.entries()]
    .map(([referrer_id, entry]) => ({
      referrer_id,
      username: usernameById.get(referrer_id) || `user_${referrer_id}`,
      total_volume: entry.volume,
      unique_referrals: entry.buyers.size,
    }))
    .filter((e) => e.total_volume > 0)
    .sort((a, b) => b.total_volume - a.total_volume);

  const metrics = await computeCompetitionMetrics(supabase, { phaseIds, start, end });
  return { leaderboard, metrics, source: 'window', phaseIds };
}

export async function computeCompetitionMetrics(supabase, opts) {
  const { start = null, end = null } = opts;
  const phaseIds = normalizePhaseIds(opts);
  if (!phaseIds.length) return emptyMetrics();

  const singlePhase = phaseIds.length === 1 ? phaseIds[0] : null;
  if (singlePhase != null && !start && !end) {
    const { data, error } = await supabase.rpc('get_phase_shares_and_metrics', {
      p_phase_id: singlePhase,
    });
    if (!error) {
      const metrics = data?.[0] || data || {};
      const totalPurchaseAmount = parseFloat(metrics.total_purchase_amount || 0);
      const conversionAmount = parseFloat(metrics.conversion_amount || 0);
      return {
        total_shares_sold: parseFloat(metrics.total_shares_sold || 0),
        conversion_shares: parseFloat(metrics.conversion_shares || 0),
        total_purchase_amount: totalPurchaseAmount,
        conversion_amount: conversionAmount,
        commissions_paid: parseFloat(metrics.commissions_paid || 0),
        total_revenue: totalPurchaseAmount + conversionAmount,
      };
    }
  }

  let purchaseQuery = supabase
    .from('aureus_share_purchases')
    .select('id, total_amount, shares_purchased, bonus_shares, price_per_share, phase_id, payment_method, created_at, competition_submitted_at')
    .in('phase_id', phaseIds)
    .in('status', ['active', 'consolidated'])
    .not('payment_method', 'in', EXCLUDED_IN_FILTER)
    // v4.8.251: same unordered-pagination defect as computeCompetitionLeaderboard above.
    // This drives the prize-pool metrics (total revenue / shares sold), so dropped rows
    // understated the pool itself, not just individual ranks.
    .order('id', { ascending: true });
  purchaseQuery = applyCompetitionTimeWindow(purchaseQuery, { start, end, phaseIds });

  let purchases = await fetchAllRows(purchaseQuery);
  purchases = purchases.filter((p) => isClubSaleMethod(p.payment_method));
  if (start || end) {
    purchases = purchases.filter((p) => purchaseInCompetitionWindow(p, start, end));
  }
  await applyClubSaleAmounts(supabase, purchases);
  let totalPurchaseAmount = 0;
  let totalSharesSold = 0;
  for (const p of purchases) {
    totalPurchaseAmount += parseFloat(p.total_amount) || 0;
    totalSharesSold += (parseFloat(p.shares_purchased) || 0) + (parseFloat(p.bonus_shares) || 0);
  }

  let conversionAmount = 0;
  let conversionShares = 0;
  try {
    const probe = await supabase
      .from('commission_conversions')
      .select('*')
      .eq('status', 'approved')
      .limit(1);
    const sample = !probe.error && probe.data?.[0] ? probe.data[0] : null;
    if (sample) {
      const hasPhase = Object.prototype.hasOwnProperty.call(sample, 'phase_id');
      const amountCol = ['conversion_amount', 'usdt_amount', 'amount_usdt', 'amount']
        .find((c) => Object.prototype.hasOwnProperty.call(sample, c));
      const sharesCol = ['shares_received', 'share_amount', 'shares']
        .find((c) => Object.prototype.hasOwnProperty.call(sample, c));
      const selectCols = ['created_at', amountCol, sharesCol].filter(Boolean).join(', ');
      if (selectCols && (amountCol || sharesCol)) {
        let conversionQuery = supabase
          .from('commission_conversions')
          .select(selectCols)
          .eq('status', 'approved');
        if (hasPhase) conversionQuery = conversionQuery.in('phase_id', phaseIds);
        if (start) conversionQuery = conversionQuery.gte('created_at', start);
        if (end) conversionQuery = conversionQuery.lte('created_at', end);
        const conversions = await fetchAllRows(conversionQuery);
        for (const c of conversions) {
          if (amountCol) conversionAmount += parseFloat(c[amountCol]) || 0;
          if (sharesCol) conversionShares += parseFloat(c[sharesCol]) || 0;
        }
      }
    }
  } catch {
    // never fail leaderboard on conversion metrics
  }

  let commissionQuery = supabase
    .from('commission_transactions')
    .select('usdt_commission')
    .in('phase_id', phaseIds)
    .eq('status', 'approved');
  if (start) commissionQuery = commissionQuery.gte('payment_date', start);
  if (end) commissionQuery = commissionQuery.lte('payment_date', end);
  const commissionRows = await fetchAllRows(commissionQuery);
  const commissionsPaid = commissionRows.reduce(
    (s, t) => s + (parseFloat(t.usdt_commission) || 0),
    0
  );

  return {
    total_shares_sold: totalSharesSold,
    conversion_shares: conversionShares,
    total_purchase_amount: totalPurchaseAmount,
    conversion_amount: conversionAmount,
    commissions_paid: commissionsPaid,
    total_revenue: totalPurchaseAmount + conversionAmount,
  };
}

function emptyMetrics() {
  return {
    total_shares_sold: 0,
    conversion_shares: 0,
    total_purchase_amount: 0,
    conversion_amount: 0,
    commissions_paid: 0,
    total_revenue: 0,
  };
}

/**
 * Resolve Diggers competition for a phase at a point in time.
 */
export function resolveCompetitionWindow(competitions, { phaseId, now = new Date() } = {}) {
  const list = (competitions || [])
    .filter((c) => !phaseId || Number(c.phase_id) === Number(phaseId))
    .filter((c) => String(c.status || '').toLowerCase() !== 'cancelled');
  if (!list.length) return null;

  const ts = now.getTime();
  const inWindow = list.filter((c) => {
    const start = c.start_date ? new Date(c.start_date).getTime() : null;
    const end = c.end_date ? new Date(c.end_date).getTime() : null;
    // Zero-width / collapsed windows never count as current
    if (start != null && end != null && end <= start) return false;
    if (start != null && ts < start) return false;
    if (end != null && ts > end) return false;
    return true;
  });

  const activeInWindow = inWindow.find((c) => c.is_active === true || c.status === 'active');
  if (activeInWindow) return activeInWindow;
  if (inWindow.length) {
    return inWindow.sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
    )[0];
  }

  // Outside all windows → most recently ended Diggers for this phase
  return list
    .slice()
    .sort((a, b) => {
      const ae = a.end_date ? new Date(a.end_date).getTime() : 0;
      const be = b.end_date ? new Date(b.end_date).getTime() : 0;
      if (be !== ae) return be - ae;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    })[0];
}

/**
 * Same ranked list the public Club board uses for Phase 5+ (r1 = index 0).
 * Round volume first, then volume desc, buyers desc, referrer_id asc.
 */
export function rankClubLeaderboard(rawLb, excludedUserIds = []) {
  const excluded = new Set(
    (excludedUserIds || []).map(Number).filter((n) => !Number.isNaN(n))
  );
  return (rawLb || [])
    .filter((e) => !excluded.has(Number(e.referrer_id)))
    .map((e) => ({
      referrer_id: Number(e.referrer_id),
      username: e.username,
      total_volume: Math.round(parseFloat(e.total_volume) || 0),
      unique_referrals: parseInt(e.unique_referrals, 10) || 0,
    }))
    .sort((a, b) => {
      if (b.total_volume !== a.total_volume) return b.total_volume - a.total_volume;
      if (b.unique_referrals !== a.unique_referrals) return b.unique_referrals - a.unique_referrals;
      return a.referrer_id - b.referrer_id;
    });
}

/** Resolve opaque Club ref (r1…) against a ranked list. */
export function resolveClubLeaderboardRef(ranked, refParam) {
  const refIndex = parseInt(String(refParam || '').replace(/^r/i, ''), 10) - 1;
  if (Number.isNaN(refIndex) || refIndex < 0 || refIndex >= ranked.length) return null;
  return ranked[refIndex];
}

/** A 7-day Club week. An extended close adds later weeks. It does not lengthen week 4. */
export const DIGGERS_WEEK_COUNT = 4;
const DIGGERS_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const DIGGERS_WEEK_CAP = 52;

/**
 * Split a weekly Diggers window into 7-day weeks.
 * The last week ends at the competition close and is shorter when that close
 * is not on a 7-day boundary. No week is longer than 7 days.
 */
export function splitCompetitionWeeks(startIso, endIso, now = new Date()) {
  if (!startIso || !endIso) return [];
  const startMs = new Date(startIso).getTime();
  const endMs = new Date(endIso).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return [];
  const nowTs = now.getTime();
  const weeks = [];
  for (let i = 0; i < DIGGERS_WEEK_CAP; i++) {
    const wStartMs = startMs + i * DIGGERS_WEEK_MS;
    if (wStartMs > endMs) break;
    const naturalEnd = wStartMs + DIGGERS_WEEK_MS - 1;
    const wEndMs = Math.min(naturalEnd, endMs);
    weeks.push({
      week: i + 1,
      start: new Date(wStartMs).toISOString(),
      end: new Date(wEndMs).toISOString(),
      empty: false,
      isCurrent: nowTs >= wStartMs && nowTs <= wEndMs,
      isPast: nowTs > wEndMs,
      isFuture: nowTs < wStartMs,
    });
    if (wEndMs >= endMs) break;
  }
  return weeks;
}

export function parseDiggersWeekQuery(weekQuery, maxWeeks = DIGGERS_WEEK_COUNT) {
  if (weekQuery == null || weekQuery === '' || String(weekQuery) === 'auto') return 'auto';
  if (String(weekQuery) === 'full') return 'full';
  const n = parseInt(String(weekQuery), 10);
  const cap = Math.max(1, Number(maxWeeks) || DIGGERS_WEEK_COUNT);
  if (n >= 1 && n <= cap) return n;
  return 'auto';
}

/**
 * Overlay a week slice on a resolved Diggers window.
 * Only Mine Production and later (ctx.weeklyEnabled) split into weeks.
 * Earlier phases always score the stored monthly/phase window.
 * week=full is not a prize view. On a weekly phase it resolves to the current week.
 * week=auto (or omitted) uses the week that contains now, or the last started week.
 * It must not fall back to the full phase, or month-long volume becomes the prize board.
 */
export function applyDiggersWeek(ctx, weekQuery, now = new Date()) {
  if (!ctx?.weeklyEnabled) {
    return {
      ...ctx,
      weeklyEnabled: false,
      weeks: [],
      week: 'full',
      scoringStart: ctx.windowStart,
      scoringEnd: ctx.windowEnd,
    };
  }
  const weeks = splitCompetitionWeeks(ctx.windowStart, ctx.windowEnd, now);
  let week = parseDiggersWeekQuery(weekQuery, weeks.length || DIGGERS_WEEK_COUNT);
  if (week === 'full') week = 'auto';
  if (week === 'auto') {
    const current = weeks.find((w) => w.isCurrent);
    if (current) {
      week = current.week;
    } else {
      const opened = weeks.filter((w) => w.isPast || w.isCurrent);
      week = opened.length ? opened[opened.length - 1].week : (weeks[0]?.week ?? 'full');
    }
  }
  if (week === 'full' || !weeks.length) {
    return {
      ...ctx,
      weeklyEnabled: true,
      weeks,
      week: 'full',
      scoringStart: ctx.windowStart,
      scoringEnd: ctx.windowEnd,
    };
  }
  const selected = weeks.find((w) => w.week === week);
  if (!selected || selected.empty) {
    return {
      ...ctx,
      weeklyEnabled: true,
      weeks,
      week: 'full',
      scoringStart: ctx.windowStart,
      scoringEnd: ctx.windowEnd,
    };
  }
  return {
    ...ctx,
    weeklyEnabled: true,
    weeks,
    week: selected.week,
    scoringStart: selected.start,
    scoringEnd: selected.end,
  };
}

/** Scoring phase ids from competition.single_pool_config.score_phase_ids */
export function scoringPhaseIdsForCompetition(competition, fallbackPhaseId) {
  const configured = competition?.single_pool_config?.score_phase_ids;
  if (Array.isArray(configured) && configured.length) {
    return configured.map(Number).filter((n) => !Number.isNaN(n));
  }
  if (fallbackPhaseId != null) return [Number(fallbackPhaseId)];
  if (competition?.phase_id != null) return [Number(competition.phase_id)];
  return [];
}

/** Diggers Club display label for investment phases */
export function diggersPhaseLabel(phase) {
  if (!phase) return '';
  // Active combined window lives on Phase 10's investment row
  if (Number(phase.phase_number) === 10 || Number(phase.id) === PHASE10_PHASE_ID) {
    return 'Birthday Extension / Phase 10';
  }
  // Phase 9 stays Phase 9 (ended Diggers) — do not rename to Birthday Extension
  if (Number(phase.phase_number) === 9 || Number(phase.id) === BIRTHDAY_EXTENSION_PHASE_ID) {
    return 'Phase 9';
  }
  return phase.phase_name || `Phase ${phase.phase_number}`;
}

/** Live Club phase. Phase 10's ended window must not stay labelled active ahead of Mine Production. */
export function isDiggersActivePhase(phase) {
  return phase?.is_active === true;
}

/**
 * User ids already paid for this Club board.
 * Weekly boards match competition_winners.award_week.
 * Monthly boards match the phase award, which has no week.
 * Display only. This does not create a payment.
 */
export function clubPrizePaidUserIds(winners, week) {
  const weekNum = Number(week);
  const weekly = Number.isInteger(weekNum) && weekNum > 0;
  const ids = new Set();
  for (const row of winners || []) {
    const userId = Number(row?.user_id);
    if (!Number.isInteger(userId)) continue;
    const stored = row?.award_week;
    if (weekly) {
      if (Number(stored) === weekNum) ids.add(userId);
    } else if (stored == null || stored === '') {
      ids.add(userId);
    }
  }
  return ids;
}
